import { useCallback, useEffect, useRef, useState } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { Button, Col, Row } from 'reactstrap';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import {
  CAMPAIGN_STATUSES,
  getCampaignStatusDisplayName,
} from '../../utils/models/Campaign';
import { getWizardTypeDisplayName } from '../../utils/models/Cycle';
import { INPUT_TYPES } from '../Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from '../Widgets/Modals/ModalEditor';

export const SOURCE_TYPE_SCRATCH = 'scratch';
export const SOURCE_TYPE_COPY = 'copy';

interface CampaignSource {
  id: number;
  name: string;
  status: string;
  wizard_type: string;
}

// react-select expects {label, value, ...} on top of whatever else an option
// carries. SelectInput's own loadOptions pipeline (convertWithCallback)
// rebuilds {label, value} from {name, object} on every option it passes
// through -- specifically value: o.object, NOT o.value -- so `object` here
// isn't optional: without it, every option's post-conversion `value` is
// undefined, and react-select's default option-matching (used internally to
// decide e.g. which option gets the keyboard-focus highlight on open) can no
// longer tell options apart, confirmed by actually reproducing that exact
// symptom (first option always highlighted) with `object` missing.
//
// Shows the cycle's type (e.g. name "Bug Repro", description "Performance
// cycle") using the same getWizardTypeDisplayName() the Cycle administration
// list's own "Type" column uses -- note this reads `wizard_type`
// (configs[WIZARD_TYPE_KEY] on the backend), not `type`, which is always "P"
// regardless of the cycle's actual Performance/Engagement/eNPS/ONA kind.
//
// Unpublished cycles are eligible sources too (CONF-586), so the status is
// appended for anything not published, letting admins tell a draft apart
// from a published cycle of the same name.
const toSourceOption = (
  source: CampaignSource,
  formatMessage: IntlShape['formatMessage']
) => {
  const typeDisplayName = getWizardTypeDisplayName(
    source.wizard_type,
    formatMessage
  );
  const description =
    source.status === CAMPAIGN_STATUSES.ACTIVE
      ? typeDisplayName
      : `${typeDisplayName} (${getCampaignStatusDisplayName(
          source.status,
          formatMessage
        )})`;
  return {
    ...source,
    description,
    label: `${source.name} - ${description}`,
    value: source,
    object: { ...source, description },
  };
};

type SourceOption = ReturnType<typeof toSourceOption>;

interface FormObject {
  source_type: string;
  source_campaign_id?: SourceOption;
}

const DEFAULT_OBJECT: FormObject = {
  source_type: SOURCE_TYPE_SCRATCH,
  source_campaign_id: undefined,
};

interface Props {
  isOpen: boolean;
  toggle: () => void;
  organizationId: number;
  onScratch: () => void;
  onCopy: (sourceCampaignId: number) => void;
}

// "Create a new cycle" modal (CONF-350): start from scratch (today's
// existing default-template flow, untouched), or copy a previous cycle's
// configuration via the new /campaigns/sources + /campaigns/clone endpoints.
// Built on ModalEditor/ValidatedForm (the app's standard modal+form system)
// rather than custom JSX -- a MULTIPLE_CHOICE input for source_type, with a
// SELECT input for source_campaign_id spliced into `inputs` only while
// "copy" is selected, matching the pattern EngagementSurveyReportNewModalEditorButton
// already uses for its own conditional fields. No `url`/`method` is passed
// to ModalEditor -- with neither set, ValidatedForm's submit path (see
// ConfirmForm.handleSubmit) skips the network call entirely and just invokes
// `callback` with the current form values directly, since campaign creation
// itself stays owned by the caller (onScratch/onCopy), same as before.
const CreateCycleModal = ({
  isOpen,
  toggle,
  organizationId,
  onScratch,
  onCopy,
}: Props) => {
  const { formatMessage } = useIntl();
  const [object, setObject] = useState<FormObject>(DEFAULT_OBJECT);
  const [sourcesCache, setSourcesCache] = useState<SourceOption[] | undefined>(
    undefined
  );

  // Today's (pre-CONF-350) flow silently copies the most recent eligible
  // campaign whenever one exists, and only falls back to scratch defaults
  // when the org has none. "Copy a previous cycle" preserves that same
  // fallback once sources are known -- but only as long as the user hasn't
  // already made their own choice while the fetch was still in flight,
  // which this ref (rather than comparing source_type, since SCRATCH is both
  // "not yet decided" and a real user choice) tracks explicitly.
  const userSelectedSourceTypeRef = useRef(false);

  // Reset to defaults whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) {
      userSelectedSourceTypeRef.current = false;
      setObject(DEFAULT_OBJECT);
      setSourcesCache(undefined);
    }
  }, [isOpen]);

  // Fetch the eligible sources as soon as the modal opens (not only once
  // "Copy" is picked) -- the default source_type itself depends on whether
  // any exist, so that has to be known upfront rather than after the user
  // has already chosen. Also default-selects the most recent source (same
  // one today's flow silently auto-picks) so the field isn't left empty,
  // forcing every admin to search for it manually.
  useEffect(() => {
    if (isOpen && !sourcesCache) {
      ConfirmAPI.sendRequestToConfirm(
        'GET',
        `campaigns/sources?organization=${organizationId}`,
        null,
        (data, error) => {
          if (error || !data?.sources) {
            setSourcesCache([]);
            return;
          }
          const options = data.sources.map((source) =>
            toSourceOption(source, formatMessage)
          );
          setSourcesCache(options);
          setObject((prev) => ({
            source_campaign_id: prev.source_campaign_id ?? options[0],
            source_type:
              !userSelectedSourceTypeRef.current && options.length > 0
                ? SOURCE_TYPE_COPY
                : prev.source_type,
          }));
        },
        null
      );
    }
  }, [isOpen, sourcesCache, organizationId, formatMessage]);

  const loadCampaignSources = useCallback(
    (query: string | undefined, callback: (options: unknown[]) => void) => {
      const options = sourcesCache ?? [];
      // SelectInput re-invokes loadOptions with the currently-selected
      // option's own name as the query whenever the menu (re)opens with
      // nothing actually typed (to keep the list "centered" on the current
      // value) -- confirmed by actually reproducing it: without this check,
      // reopening the dropdown after a source is already selected narrows
      // the list down to just that one match. That's not a real search, so
      // it's treated as "no filter" here, not passed through to the filter.
      const isImplicitReopenQuery =
        query != null && query === object.source_campaign_id?.name;
      const normalizedQuery = isImplicitReopenQuery
        ? undefined
        : query?.trim().toLowerCase();
      callback(
        normalizedQuery
          ? options.filter((option) =>
              option.name?.toLowerCase().includes(normalizedQuery)
            )
          : options
      );
    },
    [sourcesCache, object.source_campaign_id?.name]
  );

  // ValidatedForm owns the form's own internal object state, but the
  // *caller* has to mirror it externally too (matching
  // EngagementSurveyReportNewModalEditorButton's own pattern) since whether
  // the source_campaign_id input even appears in `inputs` below depends on
  // the current source_type value.
  const handleFormChange = useCallback(
    (newObject: FormObject) => {
      if (newObject.source_type !== object.source_type) {
        userSelectedSourceTypeRef.current = true;
      }
      setObject(newObject);
    },
    [object.source_type]
  );

  const handleSubmit = useCallback(
    (data: FormObject) => {
      if (data.source_type === SOURCE_TYPE_COPY) {
        if (data.source_campaign_id?.id) {
          onCopy(data.source_campaign_id.id as number);
        }
      } else {
        onScratch();
      }
    },
    [onCopy, onScratch]
  );

  const isCopySelected = object.source_type === SOURCE_TYPE_COPY;
  const hasNoEligibleSources = sourcesCache?.length === 0;
  const canContinue =
    object.source_type === SOURCE_TYPE_SCRATCH || !!object.source_campaign_id;

  const inputs = [
    {
      type: INPUT_TYPES.MULTIPLE_CHOICE,
      required: true,
      name: 'source_type',
      options: [
        {
          id: SOURCE_TYPE_SCRATCH,
          name: formatMessage({
            id: 'app.views.administration.campaign_administration_dashboard.create_cycle_modal.scratch.name',
            defaultMessage: 'Start from scratch',
          }),
          helperText: formatMessage({
            id: 'app.views.administration.campaign_administration_dashboard.create_cycle_modal.scratch.helper_text',
            defaultMessage: 'Set up each cycle option yourself.',
          }),
        },
        {
          id: SOURCE_TYPE_COPY,
          name: formatMessage({
            id: 'app.views.administration.campaign_administration_dashboard.create_cycle_modal.copy.name',
            defaultMessage: 'Copy a previous cycle',
          }),
          helperText: formatMessage({
            id: 'app.views.administration.campaign_administration_dashboard.create_cycle_modal.copy.helper_text',
            defaultMessage:
              'Copy phases, questions and configuration. The new cycle still needs its own name, dates and list of participants.',
          }),
        },
      ],
    },
    ...(isCopySelected
      ? [
          {
            type: INPUT_TYPES.SELECT,
            required: true,
            name: 'source_campaign_id',
            label: formatMessage({
              id: 'app.views.administration.campaign_administration_dashboard.create_cycle_modal.source_cycle',
              defaultMessage: 'Source cycle',
            }),
            loadOptions: loadCampaignSources,
            clearable: false,
            searchable: true,
            disabled: hasNoEligibleSources,
            placeholder: hasNoEligibleSources
              ? formatMessage({
                  id: 'app.views.administration.campaign_administration_dashboard.create_cycle_modal.source_cycle_no_cycles',
                  defaultMessage: 'No cycles available',
                })
              : undefined,
            isOptionSelected: (
              option: SourceOption,
              selectValue: SourceOption[]
            ) => selectValue.some((selected) => selected.id === option.id),
            helperText: hasNoEligibleSources
              ? formatMessage({
                  id: 'app.views.administration.campaign_administration_dashboard.create_cycle_modal.source_cycle_empty',
                  defaultMessage: 'No eligible previous cycles available yet.',
                })
              : formatMessage({
                  id: 'app.views.administration.campaign_administration_dashboard.create_cycle_modal.source_cycle_helper_text',
                  defaultMessage:
                    'All eligible previous cycles you can access are searchable.',
                }),
          },
        ]
      : []),
  ];

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      // Without this, ModalEditor only invokes `callback` after the modal
      // reports itself fully closed (onClosed, normally fired post the fade
      // transition) -- but isOpen here is owned by the caller, which reacts
      // to onScratch/onCopy by navigating away rather than by closing this
      // modal, so nothing would ever flip isOpen and the callback would
      // never fire at all.
      triggerCallbackBeforeClose
      title={formatMessage({
        id: 'app.views.administration.campaign_administration_dashboard.create_cycle_modal.title',
        defaultMessage: 'Create new cycle',
      })}
      object={object}
      onChange={handleFormChange}
      inputs={inputs}
      disabled={!canContinue}
      buttonClassName=""
      submitText={formatMessage({
        id: 'app.views.administration.campaign_administration_dashboard.create_cycle_modal.continue',
        defaultMessage: 'Continue',
      })}
      callback={handleSubmit}
      renderForm={(
        inputsJsx: React.ReactNode,
        submitButton: React.ReactNode
      ) => (
        <>
          <p className="text-muted mb-2">
            {formatMessage({
              id: 'app.views.administration.campaign_administration_dashboard.create_cycle_modal.intro',
              defaultMessage:
                'Start from scratch or reuse the setup from a previous cycle.',
            })}
          </p>
          {inputsJsx}
          <Row className="mt-4">
            <Col className="col-md-6">
              <Button
                color="light"
                className="btn-block w-100"
                onClick={toggle}
              >
                {formatMessage({
                  id: 'app.views.administration.campaign_administration_dashboard.create_cycle_modal.cancel',
                  defaultMessage: 'Cancel',
                })}
              </Button>
            </Col>
            <Col className="col-md-6">{submitButton}</Col>
          </Row>
        </>
      )}
    />
  );
};

export default CreateCycleModal;
